import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

/** What the editor hands back: a new picture to upload, or the current one to remove. */
export interface PhotoChange {
    file: File | null;
    remove: boolean;
}

/**
 * Profile picture editor (mockup settings #profilePictureTemplate): a round preview (initials when
 * there is none), Choose / Change Picture and Remove Picture. A chosen image is centre-cropped to a
 * 256 px square JPEG in the browser before it is uploaded. Used by Settings > My Account and
 * Users > Edit; the page saves the change.
 */
@Component({
    selector: 'app-photo-editor',
    template: `
        <div class="pe">
            <div class="pe-preview">
                <img *ngIf="preview" [src]="preview" alt="Profile picture preview">
                <span *ngIf="!preview" aria-hidden="true">{{initials}}</span>
            </div>
            <div class="pe-controls">
                <label class="sr-field">
                    <span>Choose profile picture</span>
                    <input #file class="pe-file" type="file" accept="image/png,image/jpeg,image/webp,image/gif" (change)="pick(file)">
                </label>
                <p class="pe-help">PNG, JPEG, or WebP, up to 5 MB.</p>
                <div class="pe-actions">
                    <button class="sr-btn" type="button" (click)="file.click()">Change Picture</button>
                    <button class="sr-btn" type="button" (click)="remove(file)" [disabled]="!preview">Remove Picture</button>
                </div>
                <p class="pe-status" role="status">{{status}}</p>
            </div>
        </div>
    `,
    // The shared settings controls (.sr-field, .sr-control, .sr-btn) are scoped per component.
    styleUrls: ['./settings-records.scss'],
    styles: [`
        .pe { display: flex; align-items: center; gap: 22px; margin: 4px 0; }
        .pe-preview { position: relative; display: grid; place-items: center; flex: 0 0 112px; width: 112px; height: 112px;
            overflow: hidden; border: 1px solid var(--im-line); border-radius: 50%; color: var(--im-ink); background: var(--im-soft);
            font-size: 34px; font-weight: 800; }
        .pe-preview img { width: 100%; height: 100%; object-fit: cover; }
        .pe-controls { min-width: 0; flex: 1; }
        .pe-file { max-width: 100%; font: inherit; font-size: 14px; }
        .pe-help { margin: 6px 0 0; color: var(--im-muted); font-size: 13px; }
        .pe-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
        .pe-status { min-height: 20px; margin: 9px 0 0; color: var(--im-muted); font-size: 13px; line-height: 1.5; }
        @media (max-width: 640px) { .pe { flex-direction: column; align-items: flex-start; gap: 16px; } .pe-preview { flex-basis: 96px; width: 96px; height: 96px; } }
    `]
})
export class PhotoEditorComponent implements OnChanges {
    @Input() name = '';
    @Input() photoUrl: string = null;
    @Output() changed = new EventEmitter<PhotoChange>();
    preview: string = null;
    status = '';
    private objectUrl: string = null;

    get initials(): string {
        return (this.name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
    }

    ngOnChanges() {
        this.reset();
    }

    /** Back to the saved picture (Cancel, or after the page saved). */
    reset() {
        this.release();
        this.preview = this.photoUrl || null;
        this.status = '';
    }

    pick(input: HTMLInputElement) {
        const file = input.files && input.files[0];
        input.value = '';
        if (!file) {
            return;
        }
        if (['image/png', 'image/jpeg', 'image/webp', 'image/gif'].indexOf(file.type) === -1 || file.size > 5 * 1024 * 1024) {
            this.status = 'Choose a PNG, JPEG, or WebP image up to 5 MB.';
            return;
        }
        this.status = 'Preparing picture...';
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onerror = () => {
            URL.revokeObjectURL(url);
            this.status = 'This image could not be opened. Choose another image.';
        };
        image.onload = () => {
            // Centre square, 256 x 256 (mockup settings-account.js).
            const canvas = document.createElement('canvas');
            canvas.width = canvas.height = 256;
            const side = Math.min(image.width, image.height);
            canvas.getContext('2d').drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, 256, 256);
            URL.revokeObjectURL(url);
            canvas.toBlob(blob => {
                if (!blob) {
                    this.status = 'This image could not be opened. Choose another image.';
                    return;
                }
                const cropped = new File([blob], 'profile-' + Date.now() + '.jpg', { type: 'image/jpeg' });
                this.release();
                this.objectUrl = URL.createObjectURL(cropped);
                this.preview = this.objectUrl;
                this.status = 'Picture ready. Save Changes to update it.';
                this.changed.emit({ file: cropped, remove: false });
            }, 'image/jpeg', 0.9);
        };
        image.src = url;
    }

    remove(input: HTMLInputElement) {
        input.value = '';
        this.release();
        this.preview = null;
        this.status = 'Picture removed from preview. Save Changes to confirm.';
        this.changed.emit({ file: null, remove: true });
    }

    private release() {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
}
